import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import styled from 'styled-components';
import { Button, Card, CardTitle, Empty, Message, Select } from '../admin/ui';
import * as Api from './EmailAdminApi';
import {
  errorText, scheduleName, upcomingAudience, upcomingCount, upcomingDateLabel, upcomingWhen,
} from './emailAdminModel';
import { eventNameFor } from '../../utils/eventName';

// The "Upcoming" view of the E-Mails screen (P5): what the automatic emails would send in the next
// 7, 14 or 31 days, grouped by Vienna date in one column (the owner's layout rule: no blocks of
// unpredictable height side by side). It is a projection from the backend's same "who is due" code
// as the sending, not a promise; the note says so. A row carries its people behind a disclosure, with
// links to the template, the schedule card and each person. Read only: no action but links.
const DAY_CHOICES = [7, 14, 31];

const Intro = styled.p`
  margin: 0 0 1rem;
  font-size: .82rem;
  line-height: 1.5;
  color: #64748b;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: .5rem;
  align-items: center;
  margin-bottom: 1rem;
  font-size: .85rem;
  font-weight: 600;
  color: #334155;
`;

const Row = styled.article`
  display: flex;
  flex-wrap: wrap;
  gap: .2rem .8rem;
  padding: .8rem 0;
  border-top: 1px solid #eef2f7;
`;

const Time = styled.span`
  flex: 0 0 4rem;
  font-weight: 800;
  color: #111D4E;
  font-variant-numeric: tabular-nums;
`;

const Body = styled.div`
  flex: 1 1 16rem;
  min-width: 0;
`;

const EventName = styled.div`
  font-weight: 700;
  color: #111D4E;
  overflow-wrap: anywhere;
`;

const Meta = styled.div`
  font-size: .8rem;
  color: #475569;
  overflow-wrap: anywhere;
`;

const RowLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: .25rem 1rem;
  margin-top: .35rem;
  font-size: .8rem;

  a {
    color: #0369a1;
    font-weight: 700;
  }
`;

const Disclosure = styled.button`
  border: 0;
  background: transparent;
  color: #0369a1;
  font: inherit;
  font-size: .8rem;
  font-weight: 700;
  padding: .3rem 0 0;
  cursor: pointer;
`;

const Names = styled.ul`
  margin: .25rem 0 0;
  padding-left: 1.1rem;
  font-size: .85rem;
  line-height: 1.5;
  overflow-wrap: anywhere;

  a {
    color: #111D4E;
    font-weight: 700;
  }
`;

const Off = styled.div`
  margin-bottom: 1rem;
  padding: .6rem .75rem;
  background: #fef3c7;
  color: #92400e;
  font-size: .85rem;
`;

const OffList = styled.ul`
  margin: .25rem 0 0;
  padding-left: 1.1rem;
`;

const UpcomingRow = ({ row }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const audience = upcomingAudience(t, row);
  const eventName = eventNameFor(intl.locale, row.eventNameDe, row.eventNameEn) || `#${row.eventId}`;

  return (
    <Row>
      <Time>{row.sendTime}</Time>
      <Body>
        <EventName>{eventName}</EventName>
        <Meta>
          {upcomingWhen(intl, row)}
          {audience ? ` · ${audience}` : ''}
        </Meta>
        <Meta>{upcomingCount(intl, row.count)}</Meta>
        <RowLinks>
          <Link to={`/admin/emails?tab=emails&key=${row.templateKey}`}>
            <FormattedMessage id="emailAdmin.upcoming.template" defaultMessage="Edit the email" />
          </Link>
          <Link to={`/admin/emails?tab=schedules#schedule-${row.kind}`}>
            <FormattedMessage id="emailAdmin.upcoming.schedule" defaultMessage="Open the schedule" />
          </Link>
        </RowLinks>
        {row.people.length > 0 && (
          <>
            <Disclosure type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
              {open
                ? t('emailAdmin.upcoming.hidePeople', 'Hide the people')
                : t('emailAdmin.upcoming.showPeople', 'Show the people')}
            </Disclosure>
            {open && (
              <Names aria-label={t('emailAdmin.upcoming.names', 'The people')}>
                {row.people.map((person) => (
                  <li key={person.personId}>
                    <Link to={`/admin/people/${person.personId}`}>{person.name}</Link>
                  </li>
                ))}
              </Names>
            )}
          </>
        )}
      </Body>
    </Row>
  );
};

const EmailUpcoming = () => {
  const intl = useIntl();
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(() => {
    setLoadError(null);
    setData(null);
    Api.fetchUpcoming(days)
      .then(setData)
      .catch(setLoadError);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loadError) {
    return (
      <Card>
        <Message error role="alert">
          {errorText(loadError, intl.formatMessage({
            id: 'emailAdmin.upcoming.loadFailed',
            defaultMessage: 'Could not load the coming emails.',
          }))}
        </Message>
        <Button type="button" secondary onClick={load}>
          <RefreshCw size={16} /><FormattedMessage id="emailAdmin.refresh" defaultMessage="Refresh" />
        </Button>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card>
        <Empty>
          <FormattedMessage id="emailAdmin.upcoming.loading" defaultMessage="Loading the coming emails…" />
        </Empty>
      </Card>
    );
  }

  const off = (data.schedules || []).filter((schedule) => !schedule.enabled);
  const groups = [];
  for (const row of data.rows || []) {
    const last = groups[groups.length - 1];
    if (!last || last.date !== row.date) groups.push({ date: row.date, rows: [row] });
    else last.rows.push(row);
  }

  return (
    <>
      <Intro>
        <FormattedMessage id="emailAdmin.upcoming.intro"
          defaultMessage="The automatic emails planned for the next {days, plural, one {# day} other {# days}}: who would get them if nothing changes until then. This is a projection, not a promise – people may answer, upload, register, or an event may move."
          values={{ days: data.days }} />
      </Intro>

      <Controls>
        <label htmlFor="upcoming-days">
          <FormattedMessage id="emailAdmin.upcoming.days.label" defaultMessage="Show" />
        </label>
        <Select id="upcoming-days" value={days} onChange={(event) => setDays(Number(event.target.value))}>
          {DAY_CHOICES.map((choice) => (
            <option key={choice} value={choice}>
              {intl.formatMessage(
                { id: 'emailAdmin.upcoming.days.option', defaultMessage: '{days} days' },
                { days: choice },
              )}
            </option>
          ))}
        </Select>
      </Controls>

      {off.length > 0 && (
        <Off role="note">
          <FormattedMessage id="emailAdmin.upcoming.offTitle" defaultMessage="Off – nothing goes out:" />
          <OffList>
            {off.map((schedule) => (
              <li key={schedule.kind}>
                {scheduleName((id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values),
                  schedule.kind)}
                {': '}
                <FormattedMessage id="emailAdmin.upcoming.off" defaultMessage="off – nothing goes out" />
              </li>
            ))}
          </OffList>
        </Off>
      )}

      {groups.length === 0 ? (
        <Card>
          <Empty>
            <FormattedMessage id="emailAdmin.upcoming.empty"
              defaultMessage="Nothing goes out in the next {days, plural, one {# day} other {# days}}."
              values={{ days: data.days }} />
          </Empty>
        </Card>
      ) : groups.map((group) => (
        <Card key={group.date}>
          <CardTitle>{upcomingDateLabel(intl, group.date, data.from)}</CardTitle>
          {group.rows.map((row) => (
            <UpcomingRow key={`${row.kind}-${row.stepId || 'invite'}-${row.eventId}-${row.audience || ''}`}
              row={row} />
          ))}
        </Card>
      ))}
    </>
  );
};

export default EmailUpcoming;
